<template>
    <div>
        <!-- Filters -->
        <b-card no-body>
            <validation-observer ref="dataForm" #default="{ invalid }">
                <b-form ref="form" @submit.stop.prevent="handleCreate">
                    <b-card-header class="pb-50">
                        <h5>Filter Location</h5>
                    </b-card-header>
                    <b-card-body>
                        <b-row>
                            <b-col cols="12">
                                <location-filters
                                    size="sm"
                                    @filter="getLocation"
                                />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="3">
                                <b-row>
                                    <b-col cols="8">
                                        <b-form-group label-for="name" label="Village Name">
                                            <validation-provider #default="{ errors }" name="name" rules="">
                                                <b-form-input
                                                    id="name"
                                                    name="name"
                                                    v-model="village.name"
                                                    :state="errors.length > 0 ? false : null"
                                                    size="sm"
                                                />
                                                <small class="text-danger">{{ errors[0] }}</small>
                                                <small class="text-danger" v-if="serverErrors && (serverErrors.name || serverErrors.location_id)">{{ serverErrors.name? serverErrors.name[0] : serverErrors.location_id[0] }}</small>
                                            </validation-provider>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="4" class="pt-2">
                                        <b-button
                                            size="sm"
                                            variant="primary"
                                            @click="submit"
                                            :disabled="saving"
                                        >
                                            <b-spinner small class="mr-1" label="Small Spinner" v-if="saving" />
                                            <span v-if="!saving">Save</span>
                                            <span v-if="saving">Saving...</span>
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </b-col>
                            <b-col cols="9">
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-form>
            </validation-observer>
        </b-card>
        <!-- ./Filters -->

        <!-- Table Container Card -->
        <b-card no-body class="mb-0">
            <div class="m-2">
                <!-- Table Top -->
                <b-row>
                    <!-- Per Page -->
                    <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                        <label>Show</label>
                        <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :options="perPageOptions" :clearable="false"
                            class="per-page-selector d-inline-block mx-50 select-size-sm" />
                        <label>entries</label>
                    </b-col>
                    <!-- ./Per Page -->

                    <!-- Search & Button -->
                    <b-col cols="12" md="6">
                        <div class="d-flex align-items-center justify-content-end">
                            <b-form-input
                                v-model="searchQuery"
                                class="d-inline-block mr-1"
                                placeholder="Search..."
                                size="sm"
                            />
                        </div>
                    </b-col>
                    <!-- ./Search & Button-->
                </b-row>
                <!-- ./Table Top -->
            </div>

            <!-- Table -->
            <b-table
                striped
                hover
                small
                ref="records"
                class="position-relative"
                :items="fetch"
                responsive
                :fields="columns"
                primary-key="id"
                :sort-by.sync="sortBy"
                show-empty
                empty-text="No records found"
                :sort-desc.sync="isSortDirDesc"
            >
                <!-- Extract columns data -->
                <template #cell(name)="data">
                    {{ data.item.name.toUpperCase() }}
                </template>

                <template #cell(location)="data">
                    {{ data.item.location.name.toUpperCase() }}
                </template>

                <!-- Column: Actions -->
                <template #cell(actions)="data">
                    <b-dropdown
                        variant="link"
                        no-caret
                        :right="$store.state.appConfig.isRTL"
                    >

                        <template #button-content>
                        <feather-icon
                            icon="MoreVerticalIcon"
                            size="16"
                            class="align-middle text-body"
                        />
                        </template>
        
                        <b-dropdown-item :to="{ path: 'growers/' + data.item.id }">
                            <feather-icon icon="EyeIcon" />
                            <span class="align-middle ml-50">View</span>
                        </b-dropdown-item>

                        <b-dropdown-item @click="invokeUpdateForm(data.item)">
                            <feather-icon icon="EditIcon" />
                            <span class="align-middle ml-50">Edit</span>
                        </b-dropdown-item>
        
                        <b-dropdown-item @click="remove(data.item.id)">
                            <feather-icon icon="TrashIcon" />
                            <span class="align-middle ml-50">Delete</span>
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </b-table>

            <!-- Table Footer -->
            <div class="mx-2 mb-2">
                <b-row>
                    <b-col
                        cols="12"
                        sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    >
                        <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
                    </b-col>
                    <!-- Pagination -->
                    <b-col
                        cols="12"
                        sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-end"
                    >
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRecords"
                            :per-page="perPage"
                            first-number
                            last-number
                            class="mb-0 mt-1 mt-sm-0"
                            prev-class="prev-item"
                            next-class="next-item"
                        >
                            <template #prev-text>
                                <feather-icon icon="ChevronLeftIcon" size="18" />
                            </template>
                            <template #next-text>
                                <feather-icon icon="ChevronRightIcon" size="18" />
                            </template>
                        </b-pagination>
                    </b-col>
                </b-row>
            </div>

        </b-card>
        <!-- ./Table Container Card -->
    </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
    BRow, BCol, BModal, VBModal, BButton, BTable,
    BBadge, BDropdown, BDropdownItem, BPagination, BSpinner, BMedia, BAvatar, BLink,
    BForm, BFormGroup, BFormSelect, BFormSelectOption, BFormInput, BFormDatepicker,
    BFormCheckbox,
    BCard, BCardHeader, BCardSubTitle, BCardBody,
 } from 'bootstrap-vue'
import { required, integer } from '@validations'
import store from '@/store'
import { ref, onUnmounted, onMounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import villagesStoreModule from '@/views/cromis/village/villagesStoreModule'
import useVillagesList from '@/views/cromis/village/useVillagesList'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import LocationFilters from '@core/components/cromis/filters/LocationFilters'

export default {
    props: {},
    components: {
        BCard,
        BCardHeader,
        BCardSubTitle,
        BCardBody,
        BRow,
        BCol,
        BForm,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BFormSelectOption,
        BFormCheckbox,
        BFormDatepicker,
        BModal,
        BButton,
        BSpinner,
        BMedia,
        BLink,
        BAvatar,
        BTable,
        BBadge,
        BDropdown,
        BDropdownItem,
        BPagination,

        vSelect,
        ValidationProvider,
        ValidationObserver,
        LocationFilters,
    },
    directives: {
        'b-modal': VBModal,
    },
    
    setup (props, context) {
        const toast = useToast()
        const dataForm = ref(null)
        const myModal = ref(null)
        const fileInput = ref(null)
        const saving = ref(false)
        const uploading = ref(false)
        const serverErrors = ref(null)

        const selections = ref([])
        const levels = ref(null)
        const children = ref([])
        const societies = ref([])
        const sectors = ref([])
        const clubs = ref([])

        const client = ref(JSON.parse(localStorage.getItem('userData')).client)

        const location = ref(null)

        const village = ref({
            id: null,
            name: null,
            location_id: null,
        })

        const CROMIS_VILLAGE_STORE_MODULE_NAME = 'cromis-village'

        // Register module
        if (!store.hasModule(CROMIS_VILLAGE_STORE_MODULE_NAME)) store.registerModule(CROMIS_VILLAGE_STORE_MODULE_NAME, villagesStoreModule)

        // UnRegister on leave
        onUnmounted(() => {
        if (store.hasModule(CROMIS_VILLAGE_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_VILLAGE_STORE_MODULE_NAME)
        })

        const isAddNewUserSidebarActive = ref(false)

        onMounted(async () => {
            // await store.dispatch('cromis-grower/levels')
            //         .then(response => {
            //             levels.value = response.data.levels[0]

            //             selections.value.push({
            //                 level: levels.value.name.toLowerCase(),
            //                 model: null,
            //                 label: levels.value.name,
            //                 child: levels.value.children.length > 0? levels.value.children[0].name.toLowerCase() : null,
            //                 locations: [],
            //             })

            //             let list = levels.value.children

            //             while(list.length > 0){
            //                 children.value.push(list[0])
            //                 if(list[0].children > 0){
            //                     selections.value.push({ level: list[0].name.toLowerCase(), model: null, label: list[0].name, child: list[0].children[0].name.toLowerCase(), locations: [] })
            //                 }
            //                 else{
            //                     selections.value.push({ level: list[0].name.toLowerCase(), model: null, label: list[0].name, child: null, locations: [] })
            //                 }

            //                 list = list[0].children
            //             }
            //         })
            //         .catch(error => {
            //             console.log('got error', error)
            //         })

            // await store.dispatch('cromis-grower/locations')
            //             .then(response => {
            //                 populateSelections(response.data.locations)
            //             })
            //             .catch(error => {
            //                 console.log('got error', error)
            //             })
        })

        const getLocation = (filteredValue) => {
            location.value = filteredValue
            village.value.location_id = location.value !== null? location.value.id : null
            locationFilter.value = location.value !== null? location.value.id : null
        }

        const {
            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            // UI
            // resolveUserRoleVariant,
            // resolveUserRoleIcon,
            resolveGrowerTypeVariant,
            resolveStatusVariant,

            // Filers
            locationFilter,
        } = useVillagesList()

        // Methods
        const invokeUpdateForm = async (item) => {
            village.value = {
                id: item.id,
                name: item.name,
                location_id: item.location.id,
            }
        }

        const invokeCreateForm = () => {
            village.value = {
                id: null,
                name: null,
                location_id: null,
            }
        }

        const isFormValid = async () => {
            let isValid = false 
            await dataForm.value.validate().then(success => {
                isValid = success
            })

            return isValid
        }

        const submit = (bvModalEvt) => {
            bvModalEvt.preventDefault()

            serverErrors.value = null
            // Handle form submit
            if (village.value.id === null || village.value.id === 0)
                handleCreate()
            else
                handleUpdate(village.value)
        }

        const handleCreate = async () => {
            saving.value = true
            const valid = await isFormValid()

            if(!valid){
                saving.value = false
                return
            }

            
            await store.dispatch('cromis-village/create', village.value)
                .then(response => {
                    refetch()
                    saving.value = false

                    village.value = {
                        id: null,
                        name: null,
                        location_id: null,
                    }

                    context.root.$swal({
                        icon: 'success',
                        text: `Village ${response.data.name} has been saved successfully!`,
                        showConfirmButton: true,
                        timer: 5000,
                        customClass: {
                            confirmButton: 'btn btn-primary',
                        },
                        buttonsStyling: false,
                    })
                })
                .catch(error => {
                    saving.value = false
                    if(error.response.status === 422){
                        serverErrors.value = error.response.data.errors
                    }
                    else{
                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'Something went wrong. See tech support',
                            showConfirmButton: true,
                            customClass: {
                                confirmButton: 'btn btn-danger',
                            },
                            buttonsStyling: false,
                        })
                    }
                })
        }

        const handleUpdate = async (item) => {
            saving.value = true
            const valid = await isFormValid()

            if(!valid){
                saving.value = false
                return
            }

            await store.dispatch('cromis-village/update', { id: item.id, data: item })
                .then(response => {
                    refetch()
                    saving.value = false

                    village.value = {
                        id: null,
                        name: null,
                        location_id: null,
                    }

                    context.root.$swal({
                        icon: 'success',
                        text: `Changes to village ${response.data.name} has been saved successfully!`,
                        showConfirmButton: false,
                        timer: 5000,
                        customClass: {
                            confirmButton: 'btn btn-success',
                        },
                        buttonsStyling: false,
                    })
                })
                .catch(error => {
                    saving.value = false
                    if(error.response.status === 422){
                        serverErrors.value = error.response.data.errors
                    }
                    else{
                        context.root.$swal({
                            icon: 'error',
                            title: 'Server Error',
                            text: 'Something went wrong. See tech support',
                            showConfirmButton: true,
                            customClass: {
                                confirmButton: 'btn btn-danger',
                            },
                            buttonsStyling: false,
                        })
                    }
                })
        }

        const remove = async (id) => {
            await store.dispatch('cromis-village/remove', id)
                        .then(response => {
                            refetch()
                        })
                        .catch(error => {
                            context.root.$swal({
                                icon: 'error',
                                title: 'Server Error',
                                text: 'Something went wrong. See tech support',
                                showConfirmButton: false,
                                customClass: {
                                    confirmButton: 'btn btn-danger',
                                },
                                buttonsStyling: false,
                            })
                        })
        }

        return {
            client,

            // Sidebar
            isAddNewUserSidebarActive,

            fetch,
            columns,
            perPage,
            currentPage,
            totalRecords,
            from,
            to,
            meta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            records,
            refetch,

            // Filter
            locationFilter,

            // UI
            // resolveUserRoleVariant,
            // resolveUserRoleIcon,
            resolveGrowerTypeVariant,
            resolveStatusVariant,

            // Form Data
            village,

            // Validations
            required,
            integer,

            // Methods and Form refs
            myModal,
            saving,
            uploading,
            serverErrors,
            dataForm,
            invokeCreateForm,
            invokeUpdateForm,
            isFormValid,
            submit,
            handleCreate,
            handleUpdate,
            remove,
            selections,
            levels,
            children,

            getLocation,
            location,
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
